/**
 * Season API module
 */

import { apiClient } from './client'
import type {
  ClubHomeEvent,
  ClubHomeEventRoles,
  ClubHomeSeason,
  ClubResponse,
} from './club'

export interface SeasonHomeTeam {
  gender?: string
  training?: string
  ageLimits?: boolean
  lowerAge?: number
  upperAge?: number
  cutoffDate?: string | null
  id: string
  seasonId?: string
  name?: string
  title?: string
  description?: string
  logo?: string
  cardImage?: string
  eventId?: string
  eventTitle?: string
  roles?: ClubHomeEventRoles
}

export interface SeasonHomeDevelopmentProgram extends ClubHomeEvent {
  programNoun?: string
}

export interface SeasonHomeStaffAssignment {
  eventId?: string
  eventTitle?: string
  roles?: string[]
}

export interface SeasonHomeStaffMember {
  poolMember?: boolean
  staffId: string
  name: string
  email?: string
  firstName?: string
  lastName?: string
  assignments?: SeasonHomeStaffAssignment[]
}

export interface SeasonHomeStats {
  participants?: number
  registrations?: number
  staff?: number
  reports?: number
  participantsByEvent?: SeasonReportEventParticipantCount[]
  manualRegistrations?: SeasonReportRegistrationGroup[]
  unactivatedRegistrations?: SeasonReportRegistrationGroup[]
}

export interface SeasonReportEventParticipantCount {
  eventId: string
  eventTitle: string
  participants: number
}

export interface SeasonReportParticipant {
  [key: string]: unknown
  participantId: string
  firstName: string
  lastName: string
  userId?: string
  participantNumber?: string | number
  preferredName?: string
  photoUrl?: string
  profileImage?: string
  imageUrl?: string
  clubName?: string
  role1?: string
  role2?: string
  fullRole1?: string
  fullRole2?: string
  metrics?: SeasonReportAthleteCardMetric[]
  attributes?: SeasonReportAthleteCardMetric[]
  details?: Record<string, unknown>
  participant?: Record<string, unknown>
  athlete?: Record<string, unknown>
  user?: Record<string, unknown>
  athleteCard?: SeasonReportAthleteCard
}

export interface SeasonReportAthleteCardMetric {
  label: string
  value: string | number
}

export interface SeasonReportAthleteCard {
  [key: string]: unknown
  participantId?: string
  participantNumber?: string | number
  firstName?: string
  preferredName?: string
  lastName?: string
  photoUrl?: string
  clubName?: string
  role1?: string
  role2?: string
  fullRole1?: string
  fullRole2?: string
  metrics?: SeasonReportAthleteCardMetric[]
  attributes?: SeasonReportAthleteCardMetric[]
  details?: Record<string, unknown> | SeasonReportAthleteCardMetric[]
  participant?: Record<string, unknown>
  athlete?: Record<string, unknown>
  user?: Record<string, unknown>
}

export interface SeasonReportRegistrationGroup {
  eventId: string
  eventTitle: string
  participants: SeasonReportParticipant[]
}

export interface SeasonHomeResponse {
  features?: {athleteDevelopment:import('./athleteDevelopment').AthleteDevelopmentFeature}
  canCreatePrograms?: boolean
  canCreateTeams?: boolean
  club: ClubResponse
  season: ClubHomeSeason
  events: ClubHomeEvent[]
  teams: SeasonHomeTeam[]
  developmentPrograms: SeasonHomeDevelopmentProgram[]
  staff?: SeasonHomeStaffMember[]
  stats?: SeasonHomeStats
}

export interface DeactivateSeasonTrialsResponse {
  seasonId: string
  deactivatedTrialIds: string[]
  deactivatedCount: number
}

export interface SeasonDetailsUpdateRequest {
  name: string
  startDate?: string
  endDate?: string
  sportId?: string
  active?: boolean
  logo?: string
}

export interface SeasonDetailsUpdateResponse {
  season: ClubHomeSeason
}

export interface SeasonDeleteResponse {
  deletedSeasonId: string
}

export interface SeasonTeamDetailsUpdateRequest {
  name: string
  description?: string
  logo?: string
}

export interface SeasonTeamDetailsUpdateResponse {
  team: SeasonHomeTeam
}

export interface SeasonLogoUploadRequest {
  filename: string
  contentType: string
}

export interface SeasonLogoUploadResponse {
  upload: {
    uploadUrl: string
    publicUrl: string
    method?: 'PUT' | 'POST'
    headers?: Record<string, string>
  }
}

export interface SeasonStaffUserSearchResult {
  userId: string
  id?: string
  firstName?: string
  lastName?: string
  email: string
}

export interface SeasonStaffUserSearchResponse {
  users: SeasonStaffUserSearchResult[]
  hasMore?: boolean
}

export interface AddSeasonStaffRequest {
  eventId: string
  userId: string
  roles: string[]
}

export interface AddSeasonStaffResponse {
  staff: SeasonHomeStaffMember[]
}

export interface InviteSeasonStaffRequest {
  eventId: string
  email: string
  firstName: string
  lastName: string
  roles: string[]
}

export interface InviteSeasonStaffResponse {
  staff: SeasonHomeStaffMember[]
}

export interface ProgramOverviewCoverage {
  [key: string]: unknown
  ZERO?: number
  NOK?: number
  OK?: number
  complete?: number
  total?: number
}

export interface ProgramOverviewStaffMember {
  staffId: string
  name: string
  roles: Record<string, boolean>
  coverage?: ProgramOverviewCoverage
}

export interface ProgramOverviewParticipant {
  participantId: string
  firstName?: string
  preferredName?: string
  lastName?: string
  name?: string
  email?: string
  participantNumber?: string | number
  number?: string | number
  offer?: string
  offerDescription?: string
  offerResponse?: string
  offerStatus?: string
  offerSessionId?: string
  offerUpdatedAt?: string
  insufficientData?: boolean
}

export interface ProgramOverviewAssignmentBucket {
  bucketId: string
  name: string
  members: ProgramOverviewParticipant[]
  coverage?: ProgramOverviewCoverage
}

export interface ProgramOverviewSession {
  sessionId: string
  title: string
  startDateTime?: string
}

export interface ProgramOverviewFeedbackSummary {
  requests: number
  outstanding: number
}

export interface ProgramOverviewEvent {
  eventId: string
  title: string
  participantNoun?: string
  recommendationLabels?: import('@/utils/recommendationLabels').RecommendationTargetLabels
  /** @deprecated Compatibility alias; use participantNoun. */
  participantPronoun?: string
  lockOffers?: boolean
  promoteOffers?: boolean
  canControlOfferLock?: boolean
  canResetForNextSession?: boolean
  feedback?: ProgramOverviewFeedbackSummary
  coverage?: ProgramOverviewCoverage
  staff: ProgramOverviewStaffMember[]
  assignmentBuckets: ProgramOverviewAssignmentBucket[]
  sessions?: ProgramOverviewSession[]
}

export interface ProgramOverviewResponse {
  events: ProgramOverviewEvent[]
}

export interface ProgramOverviewAssignmentMetadataResponse {
  participants: ProgramOverviewParticipant[]
  sessions?: ProgramOverviewSession[]
}

export interface ProgramOverviewFeedbackItem {
  id: string
  participantName?: string
  staffName?: string
  status?: string
  updatedAt?: string
  title?: string
}

export interface ProgramOverviewFeedbackResponse {
  eventId: string
  requests: ProgramOverviewFeedbackItem[]
  outstanding: ProgramOverviewFeedbackItem[]
  published?: ProgramOverviewFeedbackItem[]
}

export interface ProgramOverviewOfferStateResponse {
  events: ProgramOverviewEvent[]
}

export class SeasonAPI {
  async deactivateSeasonTrials(
    seasonId: string,
    signal?: AbortSignal
  ): Promise<DeactivateSeasonTrialsResponse> {
    return apiClient.post(
      `/v2/season/${seasonId}/trials/deactivate`,
      {},
      undefined,
      signal
    )
  }

  addSeasonStaffPoolMember(seasonId:string,body:{userId:string}|{email:string;firstName:string;lastName:string}):Promise<{staff:SeasonHomeStaffMember[]}> {
    return apiClient.post(`/v2/season/${encodeURIComponent(seasonId)}/staff-pool`,body)
  }

  loadSeasonStaff(seasonId:string,eventId:string,signal?:AbortSignal):Promise<{staff:Array<{userId:string;name:string;email:string;assigned:boolean}>}> {
    return apiClient.get(`/v2/season/${encodeURIComponent(seasonId)}/staff/loadSeasonStaff`, {eventId}, signal)
  }

  async loadSeasonHome(
    seasonId: string,
    clubId?: string,
    signal?: AbortSignal
  ): Promise<SeasonHomeResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.get(`/v2/season/${seasonId}/loadSeasonHome`, query, signal)
  }

  async updateSeasonDetails(
    seasonId: string,
    payload: SeasonDetailsUpdateRequest,
    signal?: AbortSignal
  ): Promise<SeasonDetailsUpdateResponse> {
    return apiClient.patch(
      `/v2/season/${seasonId}/details`,
      payload,
      undefined,
      signal
    )
  }

  async deleteSeason(
    seasonId: string,
    signal?: AbortSignal
  ): Promise<SeasonDeleteResponse> {
    return apiClient.delete(`/v2/season/${seasonId}`, undefined, signal)
  }

  async requestSeasonLogoUpload(
    seasonId: string,
    payload: SeasonLogoUploadRequest,
    signal?: AbortSignal
  ): Promise<SeasonLogoUploadResponse> {
    return apiClient.post(
      `/v2/season/${seasonId}/logoUpload`,
      payload,
      undefined,
      signal
    )
  }

  async updateTeamDetails(
    seasonId: string,
    teamId: string,
    payload: SeasonTeamDetailsUpdateRequest,
    signal?: AbortSignal
  ): Promise<SeasonTeamDetailsUpdateResponse> {
    return apiClient.patch(
      `/v2/season/${seasonId}/teams/${teamId}/details`,
      payload,
      undefined,
      signal
    )
  }

  async searchSeasonStaffUsers(
    seasonId: string,
    query: string,
    eventId?: string,
    limit?: number,
    signal?: AbortSignal
  ): Promise<SeasonStaffUserSearchResponse> {
    const params: Record<string, unknown> = { query }
    if (eventId) params.eventId = eventId
    if (limit) params.limit = limit
    return apiClient.get(
      `/v2/season/${seasonId}/staff/userSearch`,
      params,
      signal
    )
  }

  async addSeasonStaff(
    seasonId: string,
    payload: AddSeasonStaffRequest,
    signal?: AbortSignal
  ): Promise<AddSeasonStaffResponse> {
    return apiClient.post(
      `/v2/season/${seasonId}/staff`,
      payload,
      undefined,
      signal
    )
  }

  async inviteSeasonStaff(
    seasonId: string,
    payload: InviteSeasonStaffRequest,
    signal?: AbortSignal
  ): Promise<InviteSeasonStaffResponse> {
    return apiClient.post(
      `/v2/season/${seasonId}/staff/invitations`,
      payload,
      undefined,
      signal
    )
  }

  async loadProgramOverview(
    seasonId: string,
    clubId?: string,
    signal?: AbortSignal
  ): Promise<ProgramOverviewResponse> {
    const query = clubId ? { clubId } : undefined
    return apiClient.get(
      `/v2/season/${seasonId}/programOverview`,
      query,
      signal
    )
  }

  async loadProgramOverviewAssignmentMetadata(
    eventId: string,
    signal?: AbortSignal
  ): Promise<ProgramOverviewAssignmentMetadataResponse> {
    return apiClient.get(
      `/v2/program-overview/events/${eventId}/assignmentMetadata`,
      undefined,
      signal
    )
  }

  async loadProgramOverviewFeedback(
    eventId: string,
    signal?: AbortSignal
  ): Promise<ProgramOverviewFeedbackResponse> {
    return apiClient.get(
      `/v2/program-overview/events/${eventId}/feedback`,
      undefined,
      signal
    )
  }

  async lockProgramOverviewOffers(
    eventIds: string[],
    signal?: AbortSignal
  ): Promise<ProgramOverviewOfferStateResponse> {
    return apiClient.patch(
      '/v2/program-overview/offers/lock',
      { eventIds },
      undefined,
      signal
    )
  }

  async unlockProgramOverviewOffers(
    eventIds: string[],
    signal?: AbortSignal
  ): Promise<ProgramOverviewOfferStateResponse> {
    return apiClient.patch(
      '/v2/program-overview/offers/unlock',
      { eventIds },
      undefined,
      signal
    )
  }

  async resetProgramOverviewForNextSession(
    eventIds: string[],
    signal?: AbortSignal
  ): Promise<ProgramOverviewOfferStateResponse> {
    return apiClient.patch(
      '/v2/program-overview/offers/resetForNextSession',
      { eventIds },
      undefined,
      signal
    )
  }
}

export const seasonAPI = new SeasonAPI()
